/* eslint-disable react-refresh/only-export-components */

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import type { ICompanyMapping } from '@/api/constants-mapping/types';
import { DataTable } from '@/components/shared/DataTable';
import type { FilterDef } from '@/components/shared/DataTable/types';
import {
  AssignValue,
  TextValue,
  UpdatedAtValue,
} from '@/components/shared/ValueRenderers';
import { EmptyState } from '@/components/ui/empty-state';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';
import {
  companiesMappingKeys,
  useCompaniesDropdown,
  useCompaniesMapping,
  usePatchCompanyClientId,
} from '../../../-hooks/useCompaniesMapping';

export const Route = createFileRoute(
  '/(client)/constants-mapping/(pages)/(list)/companies/',
)({
  component: CompaniesPage,
});

function CompaniesPage() {
  const { getTranslation: t } = useTranslator({ page: 'constantsMapping' });
  const queryClient = useQueryClient();
  const table = useTable('constants-mapping-companies');

  const { data, isFetching, isError } = useCompaniesMapping({
    page: table.page,
    limit: table.limit,
    ...(table.queryParams.sort !== undefined
      ? { sort: table.queryParams.sort }
      : {}),
    ...(table.queryParams.filter !== undefined
      ? { filter: table.queryParams.filter }
      : {}),
  });
  const { data: dropdownData } = useCompaniesDropdown();
  const patchMutation = usePatchCompanyClientId();

  if (isError)
    return (
      <EmptyState
        title={t('error.title')}
        description={t('error.description')}
      />
    );

  const companiesOptions = (dropdownData?.data ?? []).map(c => ({
    key: c.name,
    label: c.name,
  }));

  const columns: ColumnDef<ICompanyMapping>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('companies.columns.name'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'id',
      accessorKey: 'id',
      header: t('companies.columns.retracedId'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'internalCompanyCode',
      accessorKey: 'internalCompanyCode',
      header: t('companies.columns.myClientId'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'internalCompanyCode',
      },
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<AssignValue />}
        />
      ),
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: t('columns.updatedAt'),
      enableSorting: true,
      cell: ({ row }) => <UpdatedAtValue value={row.original.updatedAt} />,
    },
  ];

  const filterDefs: FilterDef<ICompanyMapping>[] = [
    {
      id: 'name',
      label: t('companies.columns.name'),
      type: 'select',
      options: companiesOptions,
    },
  ];

  const handleCellCommit = async (
    _rowIndex: number,
    _colId: string,
    rawValue: unknown,
    row: ICompanyMapping,
  ) => {
    try {
      await patchMutation.mutateAsync({
        forCompanyId: row.id,
        onBehalfOfCompanyId: row.onBehalfOfCompanyId,
        value: String(rawValue ?? ''),
      });
      void queryClient.invalidateQueries({
        queryKey: companiesMappingKeys.all(),
      });
    } catch {
      toast.error(t('toast.saveError'));
      throw new Error('patch failed');
    }
  };

  return (
    <DataTable
      columns={columns}
      data={data?.data ?? []}
      loading={isFetching}
      storageKey='constants-mapping-companies'
      sorting={table.sorting}
      onSortingChange={table.onSortingChange}
      filters={table.filters}
      onFiltersChange={table.onFiltersChange}
      filterDefs={filterDefs}
      pagination={{ pageIndex: table.page - 1, pageSize: table.limit }}
      onPaginationChange={table.onPaginationChange}
      manualRowCount={data?.meta?.totalCount ?? 0}
      manualPageCount={Math.ceil((data?.meta?.totalCount ?? 0) / table.limit)}
      rowIdKey='id'
      onCellCommit={handleCellCommit}
      emptyState={
        <EmptyState
          title={t('companies.empty.title')}
          description={t('companies.empty.description')}
        />
      }
    />
  );
}
